import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { Snackbar, Alert, Box, Button, Stack, Typography } from "@mui/material";
import {
  NOTIFICATION_TYPES,
  isNotificationType,
  DEFAULT_DEDUPE_TTL_MS,
} from "../notifications/types";
import {
  buildDefaultNotificationPrefs,
  normalizePrefsPartial,
  parseNotificationPrefsFromApi,
} from "../notifications/prefsUtils";
import {
  NOTIFICATION_PREFS_KEY,
  NOTIFICATION_INBOX_LOCAL_KEY,
  NOTIFICATION_BANNER_DISMISS_KEY,
} from "../notifications/storageKeys";
import { fetchData } from "../api/client";
import {
  unlockNotificationAudio,
  isNotificationAudioUnlocked,
  playSoftMessageBeep,
  playMp3Url,
  getRequestAlertSoundUrl,
} from "../notifications/soundUtils";
import {
  appendOperatorNotification,
  fetchOperatorNotifications,
  getRequestDetails,
  getConversationById,
  markOperatorNotificationsRead,
} from "../api/client";

const BATCH_MS = 3200;
const SOUND_COOLDOWN_MS = 2800;
const MAX_INBOX_LOCAL = 80;
const MAX_TOASTS = 4;
const REQUEST_META_TTL_MS = 10 * 60 * 1000;
const ASSIGNMENT_CACHE_TTL_MS = 45_000;

/**
 * Mesma regra conceitual de `emitMessageSocketToLeadUsers` (xpro4api): com responsável humano,
 * só ele deve ser alertado; em fila (sem humano/bot/IA como “dono”) o socket já vai para quem tem permissão.
 */
function shouldNotifyChatMessageForCurrentUser(assignment, currentUserId) {
  if (currentUserId == null || !Number.isFinite(Number(currentUserId))) {
    return true;
  }
  if (!assignment) {
    return true;
  }
  const me = Number(currentUserId);
  const assigneeId =
    assignment.userId != null && Number(assignment.userId) > 0
      ? Number(assignment.userId)
      : null;
  const assigneeUra = Number(assignment.userUra) || 0;
  const assigneeIsHuman = assigneeId != null && assigneeUra !== 1;

  const rawAgent =
    assignment.agentId != null && assignment.agentId !== ""
      ? String(assignment.agentId).trim()
      : "";
  const hasAiAgent = rawAgent !== "" && rawAgent !== "0";
  const assigneeIsChatbot = assigneeId != null && assigneeUra === 1;

  const isQueueForNotifications =
    !assigneeIsHuman && !assigneeIsChatbot && !hasAiAgent;

  if (assigneeIsHuman && assigneeId) {
    return assigneeId === me;
  }
  if (isQueueForNotifications) {
    return true;
  }
  return true;
}

function loadPrefs() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_PREFS_KEY);
    if (!raw) return buildDefaultNotificationPrefs();
    const p = JSON.parse(raw);
    return normalizePrefsPartial(p);
  } catch {
    return buildDefaultNotificationPrefs();
  }
}

function unwrapMePayload(d) {
  return d && typeof d === "object" && "data" in d ? d.data : d;
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(NOTIFICATION_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

function loadLocalInbox() {
  try {
    const raw = localStorage.getItem(NOTIFICATION_INBOX_LOCAL_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveLocalInbox(items) {
  try {
    localStorage.setItem(
      NOTIFICATION_INBOX_LOCAL_KEY,
      JSON.stringify(items.slice(0, MAX_INBOX_LOCAL))
    );
  } catch {
    /* ignore */
  }
}

function parseConversationIdFromDeepLink(deepLink) {
  if (!deepLink || typeof deepLink !== "string") return null;
  const m = deepLink.match(/\/chat\/(\d+)/);
  return m ? String(m[1]) : null;
}

function absoluteUrlForWindowOpen(link) {
  if (!link || typeof link !== "string") return "";
  try {
    return new URL(link, window.location.origin).href;
  } catch {
    return link;
  }
}

const NotificationContext = createContext(null);

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return ctx;
}

/** Para hooks compartilhados (ex.: useChatState) quando o provider pode não existir em testes. */
export function useNotificationsOptional() {
  return useContext(NotificationContext);
}

function severityForType(type) {
  switch (type) {
    case NOTIFICATION_TYPES.security:
      return "error";
    case NOTIFICATION_TYPES.billing:
      return "warning";
    case NOTIFICATION_TYPES.new_request:
    case NOTIFICATION_TYPES.request_assigned:
      return "info";
    default:
      return "success";
  }
}

export function NotificationProvider({ children }) {
  const navigate = useNavigate();
  const { t } = useTranslation("notifications");
  const queryClient = useQueryClient();

  const [prefs, setPrefsState] = useState(loadPrefs);
  const [inbox, setInbox] = useState(loadLocalInbox);
  const [toasts, setToasts] = useState([]);
  const [bannerOpen, setBannerOpen] = useState(false);
  /** Indicador persistente (ex.: cor do sino) até salvar com sucesso. */
  const [prefsSyncFailed, setPrefsSyncFailed] = useState(false);
  /** Snackbar pode fechar por tempo sem esconder o estado de falha no sino. */
  const [prefsSyncSnackOpen, setPrefsSyncSnackOpen] = useState(false);

  const dedupeRef = useRef(new Map());
  const lastSoundAtRef = useRef({});
  const batchRef = useRef({ timer: null, pending: new Map() });
  const inboxRef = useRef(inbox);
  const requestMetaRef = useRef(new Map()); // conversationId -> { data, fetchedAt }
  const requestMetaInFlightRef = useRef(new Map()); // conversationId -> Promise

  const bearer =
    typeof window !== "undefined" ? window.localStorage.getItem("bearerToken") : null;
  const { data: meRes } = useQuery(
    ["me"],
    () => fetchData("/users/me"),
    {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      enabled: Boolean(bearer),
    }
  );

  const meUserId = useMemo(() => {
    const wrap = meRes || {};
    const inner = unwrapMePayload(wrap.data);
    const user = inner && typeof inner === "object" && inner.id != null ? inner : null;
    if (!user) return null;
    const n = Number(user.id);
    return Number.isFinite(n) ? n : null;
  }, [meRes]);

  const assignmentCacheRef = useRef(new Map());

  const loadConversationAssignment = useCallback(async (conversationId) => {
    const cid = String(conversationId || "").trim();
    if (!cid) return null;
    const now = Date.now();
    const hit = assignmentCacheRef.current.get(cid);
    if (hit && now - hit.at < ASSIGNMENT_CACHE_TTL_MS) {
      return hit;
    }
    try {
      const raw = await getConversationById(cid);
      const payload = raw?.data ?? raw;
      const row = {
        userId: payload?.userId ?? null,
        userUra: payload?.userUra ?? null,
        agentId: payload?.agentId ?? null,
        at: now,
      };
      assignmentCacheRef.current.set(cid, row);
      return row;
    } catch {
      return null;
    }
  }, []);

  const didHydrateServerPrefsRef = useRef(false);
  const userEditedPrefsRef = useRef(false);
  const persistPrefsTimerRef = useRef(null);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  useEffect(() => {
    inboxRef.current = inbox;
  }, [inbox]);

  const runPersistPrefs = useCallback(
    async (next) => {
      try {
        await fetchData("/users/me", "PUT", { notification_prefs_json: next });
        queryClient.invalidateQueries(["me"]);
        setPrefsSyncFailed(false);
        setPrefsSyncSnackOpen(false);
      } catch {
        setPrefsSyncFailed(true);
        setPrefsSyncSnackOpen(true);
      }
    },
    [queryClient]
  );

  const dismissPrefsSyncError = useCallback(() => {
    setPrefsSyncFailed(false);
    setPrefsSyncSnackOpen(false);
  }, []);

  const retryPrefsSync = useCallback(() => {
    void runPersistPrefs(prefsRef.current);
  }, [runPersistPrefs]);

  const flushPersistPrefsNow = useCallback(async () => {
    if (!persistPrefsTimerRef.current) return;
    clearTimeout(persistPrefsTimerRef.current);
    persistPrefsTimerRef.current = null;
    if (typeof window === "undefined" || !window.localStorage.getItem("bearerToken")) return;
    await runPersistPrefs(prefsRef.current);
  }, [runPersistPrefs]);

  useEffect(() => {
    return () => {
      if (persistPrefsTimerRef.current) {
        clearTimeout(persistPrefsTimerRef.current);
        persistPrefsTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") void flushPersistPrefsNow();
    };
    window.addEventListener("pagehide", flushPersistPrefsNow);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      window.removeEventListener("pagehide", flushPersistPrefsNow);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, [flushPersistPrefsNow]);

  const schedulePersistPrefs = useCallback(
    (next) => {
      if (typeof window === "undefined" || !window.localStorage.getItem("bearerToken")) return;
      if (persistPrefsTimerRef.current) clearTimeout(persistPrefsTimerRef.current);
      persistPrefsTimerRef.current = window.setTimeout(async () => {
        persistPrefsTimerRef.current = null;
        await runPersistPrefs(next);
      }, 700);
    },
    [runPersistPrefs]
  );

  useEffect(() => {
    if (!bearer) {
      didHydrateServerPrefsRef.current = false;
      userEditedPrefsRef.current = false;
      setPrefsSyncFailed(false);
      setPrefsSyncSnackOpen(false);
      return;
    }
    if (didHydrateServerPrefsRef.current) return;
    if (userEditedPrefsRef.current) {
      didHydrateServerPrefsRef.current = true;
      return;
    }
    const wrap = meRes || {};
    const inner = unwrapMePayload(wrap.data);
    const user = inner && typeof inner === "object" && inner.id ? inner : null;
    if (!user) return;
    didHydrateServerPrefsRef.current = true;
    const raw = user.notification_prefs_json;
    if (raw == null || raw === "") return;
    const parsed = parseNotificationPrefsFromApi(raw);
    if (!parsed) return;
    setPrefsState(parsed);
    savePrefs(parsed);
  }, [bearer, meRes]);

  const setPrefs = useCallback(
    (updater) => {
      userEditedPrefsRef.current = true;
      setPrefsState((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        savePrefs(next);
        schedulePersistPrefs(next);
        return next;
      });
    },
    [schedulePersistPrefs]
  );

  const upsertMetaIntoInbox = useCallback((conversationId, meta) => {
    if (!conversationId || !meta) return;
    setInbox((prev) => {
      const next = prev.map((x) => {
        const cid = x.conversationId || parseConversationIdFromDeepLink(x.deepLink);
        if (String(cid || "") !== String(conversationId)) return x;
        if (x.requestMeta && x.requestMeta.protocol === meta.protocol) return x;
        return { ...x, conversationId: String(conversationId), requestMeta: meta };
      });
      saveLocalInbox(next);
      return next;
    });
  }, []);

  const ensureRequestMeta = useCallback(
    async (conversationId) => {
      const cid = String(conversationId || "").trim();
      if (!cid) return null;
      const cached = requestMetaRef.current.get(cid);
      if (cached && Date.now() - cached.fetchedAt < REQUEST_META_TTL_MS) {
        return cached.data;
      }
      if (requestMetaInFlightRef.current.has(cid)) {
        return requestMetaInFlightRef.current.get(cid);
      }
      const p = (async () => {
        try {
          const d = await getRequestDetails(cid);
          const meta = d
            ? {
                protocol: String(d.id || cid),
                clientName: d.cliente_nome ? String(d.cliente_nome) : "",
                typeName: d.tipo ? String(d.tipo) : "",
                subtype1: d.subtipo1 ? String(d.subtipo1) : "",
                subtype2: d.subtipo2 ? String(d.subtipo2) : "",
                subtype3: d.subtipo3 ? String(d.subtipo3) : "",
                originName: d.origem ? String(d.origem) : "",
              }
            : null;
          if (meta) {
            requestMetaRef.current.set(cid, { data: meta, fetchedAt: Date.now() });
            upsertMetaIntoInbox(cid, meta);
          }
          return meta;
        } catch {
          return null;
        } finally {
          requestMetaInFlightRef.current.delete(cid);
        }
      })();
      requestMetaInFlightRef.current.set(cid, p);
      return p;
    },
    [upsertMetaIntoInbox]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchOperatorNotifications(50);
        if (cancelled || !Array.isArray(rows) || rows.length === 0) return;
        setInbox((prev) => {
          const byId = new Map(prev.map((x) => [x.id, x]));
          for (const r of rows) {
            const id = `srv-${r.id}`;
            if (!byId.has(id)) {
              const cid = parseConversationIdFromDeepLink(r.deep_link);
              const evType = isNotificationType(r.event_type)
                ? r.event_type
                : NOTIFICATION_TYPES.system;
              byId.set(id, {
                id,
                serverId: r.id,
                type: evType,
                title: r.title || "",
                body: r.body || "",
                deepLink: r.deep_link || null,
                conversationId: cid,
                read: Boolean(r.read_at),
                createdAt: r.created_at || new Date().toISOString(),
                openLinkInNewTab: evType === NOTIFICATION_TYPES.legacy_socket,
              });
            }
          }
          const merged = Array.from(byId.values()).sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          saveLocalInbox(merged);
          return merged.slice(0, MAX_INBOX_LOCAL);
        });
      } catch {
        /* offline / rota ainda indisponível */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // tenta enriquecer metadados das solicitações visíveis (sem travar a UI)
    const ids = Array.from(
      new Set(
        inbox
          .map((x) => x.conversationId || parseConversationIdFromDeepLink(x.deepLink))
          .filter(Boolean)
          .map(String)
      )
    ).slice(0, 12);
    ids.forEach((cid) => {
      void ensureRequestMeta(cid);
    });
  }, [ensureRequestMeta, inbox]);

  useEffect(() => {
    try {
      if (localStorage.getItem(NOTIFICATION_BANNER_DISMISS_KEY) === "1") return;
      if (Notification.permission === "granted") return;
      setBannerOpen(true);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const onInteract = () => unlockNotificationAudio();
    window.addEventListener("click", onInteract, { passive: true });
    window.addEventListener("keydown", onInteract, { passive: true });
    return () => {
      window.removeEventListener("click", onInteract);
      window.removeEventListener("keydown", onInteract);
    };
  }, []);

  const shouldPlaySound = useCallback(
    (type, isConversationFocused) => {
      if (!prefs.soundEnabled) return false;
      if (prefs.soundOnlyBackground) {
        if (document.visibilityState === "visible" && isConversationFocused) return false;
      }
      const now = Date.now();
      const last = lastSoundAtRef.current[type] || 0;
      if (now - last < SOUND_COOLDOWN_MS) return false;
      lastSoundAtRef.current[type] = now;
      return true;
    },
    [prefs.soundEnabled, prefs.soundOnlyBackground]
  );

  const playSoundForType = useCallback((type) => {
    if (!isNotificationAudioUnlocked()) return;
    if (
      type === NOTIFICATION_TYPES.new_request ||
      type === NOTIFICATION_TYPES.request_assigned
    ) {
      playMp3Url(getRequestAlertSoundUrl(), 0.45);
      return;
    }
    if (type === NOTIFICATION_TYPES.chat_message) {
      playSoftMessageBeep();
      return;
    }
    playSoftMessageBeep();
  }, []);

  const pushToast = useCallback((item) => {
    const id = item.id || `toast-${Date.now()}`;
    setToasts((prev) => {
      const next = [...prev, { ...item, id, open: true }];
      return next.slice(-MAX_TOASTS);
    });
    window.setTimeout(() => {
      setToasts((prev) => prev.map((x) => (x.id === id ? { ...x, open: false } : x)));
    }, 5200);
  }, []);

  const dispatchRaw = useCallback(
    (payload) => {
      const {
        type: rawType,
        title,
        body = "",
        conversationId,
        deepLink,
        dedupeKey,
        skipSound,
        skipDesktop,
        openLinkInNewTab = false,
      } = payload;

      const type = isNotificationType(rawType) ? rawType : NOTIFICATION_TYPES.system;
      if (prefs.categories[type] === false) return;

      const key = dedupeKey || `${type}-${conversationId || ""}-${title}`;
      const now = Date.now();
      const prevT = dedupeRef.current.get(key);
      if (prevT && now - prevT < DEFAULT_DEDUPE_TTL_MS) return;
      dedupeRef.current.set(key, now);

      const isConvFocused =
        Boolean(conversationId) &&
        typeof window !== "undefined" &&
        window.location.pathname.includes(`/chat/${conversationId}`);

      const id = `local-${now}-${Math.random().toString(36).slice(2, 9)}`;
      const link =
        deepLink ||
        (conversationId ? `/chat/${conversationId}` : null);

      const cid = conversationId != null ? String(conversationId) : parseConversationIdFromDeepLink(link);
      const cachedMeta = cid ? requestMetaRef.current.get(String(cid))?.data : null;
      const entry = {
        id,
        type,
        title,
        body,
        deepLink: link,
        conversationId: cid,
        requestMeta: cachedMeta || null,
        read: false,
        createdAt: new Date().toISOString(),
        openLinkInNewTab: Boolean(openLinkInNewTab),
      };

      setInbox((prev) => {
        const next = [entry, ...prev].slice(0, MAX_INBOX_LOCAL);
        saveLocalInbox(next);
        return next;
      });

      pushToast({
        id,
        severity: severityForType(type),
        title,
        message: body,
        deepLink: link,
        openLinkInNewTab: Boolean(openLinkInNewTab),
      });

      if (cid) {
        void ensureRequestMeta(cid);
      }

      if (!skipSound && shouldPlaySound(type, isConvFocused)) {
        playSoundForType(type);
      }

      const wantDesktop =
        !skipDesktop &&
        prefs.desktopEnabled &&
        Notification.permission === "granted" &&
        !(document.visibilityState === "visible" && isConvFocused);

      if (wantDesktop && "Notification" in window) {
        try {
          const n = new Notification(title, {
            body: body || undefined,
            tag: key,
          });
          n.onclick = () => {
            window.focus();
            if (link) {
              if (openLinkInNewTab) {
                const url = absoluteUrlForWindowOpen(link);
                if (url) window.open(url, "_blank", "noopener,noreferrer");
              } else {
                navigate(link);
              }
            }
            n.close();
          };
        } catch {
          /* ignore */
        }
      }

      appendOperatorNotification({
        event_type: type,
        title,
        body,
        deep_link: link,
      }).catch(() => {});
    },
    [ensureRequestMeta, navigate, playSoundForType, prefs, pushToast, shouldPlaySound]
  );

  const lastMetaRef = useRef(new Map());

  const flushBatch = useCallback(() => {
    const run = async () => {
      const { pending } = batchRef.current;
      batchRef.current.timer = null;
      if (!pending.size) return;

      const convIds = [...pending.keys()];
      pending.clear();

      const filtered = [];
      for (const cid of convIds) {
        const meta = lastMetaRef.current.get(cid) || {};
        lastMetaRef.current.delete(cid);
        const ev = meta.eventName || "message";

        if (ev === "request") {
          filtered.push({ cid, meta });
          continue;
        }
        const assignment = await loadConversationAssignment(cid);
        if (shouldNotifyChatMessageForCurrentUser(assignment, meUserId)) {
          filtered.push({ cid, meta });
        }
      }

      if (!filtered.length) return;

      if (filtered.length === 1) {
        const { cid, meta } = filtered[0];
        if (meta.isConversationSelected && document.visibilityState === "visible") {
          return;
        }

        const ev = meta.eventName || "message";
        const type =
          ev === "request" ? NOTIFICATION_TYPES.new_request : NOTIFICATION_TYPES.chat_message;
        dispatchRaw({
          type,
          title:
            type === NOTIFICATION_TYPES.new_request
              ? t("titles.newRequest")
              : t("titles.newMessage"),
          body: t("bodies.singleConversation", { id: cid }),
          conversationId: cid,
          dedupeKey: `batch-${type}-${cid}`,
        });
        return;
      }

      const requestItems = filtered.filter(
        (x) => (x.meta.eventName || "message") === "request"
      );
      const chatItems = filtered.filter(
        (x) => (x.meta.eventName || "message") !== "request"
      );

      for (const { cid, meta } of requestItems) {
        if (meta.isConversationSelected && document.visibilityState === "visible") {
          continue;
        }
        dispatchRaw({
          type: NOTIFICATION_TYPES.new_request,
          title: t("titles.newRequest"),
          body: t("bodies.singleConversation", { id: cid }),
          conversationId: cid,
          dedupeKey: `batch-${NOTIFICATION_TYPES.new_request}-${cid}`,
        });
      }

      if (!chatItems.length) return;

      if (chatItems.length === 1) {
        const { cid, meta } = chatItems[0];
        if (meta.isConversationSelected && document.visibilityState === "visible") {
          return;
        }
        dispatchRaw({
          type: NOTIFICATION_TYPES.chat_message,
          title: t("titles.newMessage"),
          body: t("bodies.singleConversation", { id: cid }),
          conversationId: cid,
          dedupeKey: `batch-${NOTIFICATION_TYPES.chat_message}-${cid}`,
        });
        return;
      }

      const chatIds = chatItems.map((x) => x.cid);
      dispatchRaw({
        type: NOTIFICATION_TYPES.chat_message,
        title: t("titles.batchTitle"),
        body: t("bodies.multiConversations", { count: chatIds.length }),
        dedupeKey: `batch-multi-${chatIds.sort().join(",")}-${Date.now()}`,
      });
    };

    void run();
  }, [dispatchRaw, t, loadConversationAssignment, meUserId]);

  const queueSocketActivity = useCallback(
    ({ eventName, conversationId, isConversationSelected }) => {
      if (eventName === "messagesReload") return;
      const cid = String(conversationId);
      if (!cid || cid === "undefined" || cid === "null") return;

      const pending = batchRef.current.pending;
      pending.set(cid, true);
      lastMetaRef.current.set(cid, {
        eventName,
        isConversationSelected: Boolean(isConversationSelected),
      });

      if (batchRef.current.timer) {
        clearTimeout(batchRef.current.timer);
      }
      batchRef.current.timer = window.setTimeout(flushBatch, BATCH_MS);
    },
    [flushBatch]
  );

  const dispatchFromSocketLegacy = useCallback(
    (payload) => {
      const mensagem =
        typeof payload?.mensagem === "string"
          ? payload.mensagem
          : payload?.body || "";
      const link = payload?.link || payload?.deepLink || null;
      dispatchRaw({
        type: NOTIFICATION_TYPES.legacy_socket,
        title: `${t("titles.legacy")}${t("titles.legacyXpro2Suffix")}`,
        body: mensagem.slice(0, 280),
        deepLink: link,
        dedupeKey: `legacy-${link || ""}-${mensagem.slice(0, 40)}`,
        openLinkInNewTab: true,
      });
    },
    [dispatchRaw, t]
  );

  const requestDesktopPermission = useCallback(async () => {
    if (!("Notification" in window)) return "unsupported";
    const r = await Notification.requestPermission();
    if (r === "granted") {
      setPrefs((p) => ({ ...p, desktopEnabled: true }));
    }
    return r;
  }, [setPrefs]);

  const markRead = useCallback(async (ids) => {
    const serverIds = ids
      .map((id) => {
        const m = String(id).match(/^srv-(\d+)$/);
        return m ? Number(m[1]) : null;
      })
      .filter((x) => x != null);

    setInbox((prev) => {
      const next = prev.map((x) => (ids.includes(x.id) ? { ...x, read: true } : x));
      saveLocalInbox(next);
      return next;
    });

    if (serverIds.length) {
      try {
        await markOperatorNotificationsRead(serverIds);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const markAllRead = useCallback(async () => {
    let serverIds = [];
    setInbox((prev) => {
      serverIds = prev.map((x) => x.serverId).filter((x) => x != null);
      const next = prev.map((x) => ({ ...x, read: true }));
      saveLocalInbox(next);
      return next;
    });
    if (serverIds.length) {
      try {
        await markOperatorNotificationsRead(serverIds);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const unreadCount = useMemo(
    () => inbox.filter((x) => !x.read).length,
    [inbox]
  );

  const dismissBanner = useCallback(() => {
    try {
      localStorage.setItem(NOTIFICATION_BANNER_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setBannerOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      prefs,
      setPrefs,
      inbox,
      unreadCount,
      dispatch: dispatchRaw,
      dispatchRealtimeSocket: queueSocketActivity,
      dispatchFromSocketLegacy,
      markRead,
      markAllRead,
      requestDesktopPermission,
      bannerOpen,
      dismissBanner,
      prefsSyncFailed,
      dismissPrefsSyncError,
      retryPrefsSync,
    }),
    [
      prefs,
      setPrefs,
      inbox,
      unreadCount,
      dispatchRaw,
      queueSocketActivity,
      dispatchFromSocketLegacy,
      markRead,
      markAllRead,
      requestDesktopPermission,
      bannerOpen,
      dismissBanner,
      prefsSyncFailed,
      dismissPrefsSyncError,
      retryPrefsSync,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={prefsSyncSnackOpen}
        autoHideDuration={8000}
        onClose={(_, reason) => {
          if (reason === "clickaway" || reason === "timeout") setPrefsSyncSnackOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: { xs: 96, sm: 24 } }}
      >
        <Alert
          severity="warning"
          variant="filled"
          onClose={() => setPrefsSyncSnackOpen(false)}
          action={
            <Button color="inherit" size="small" sx={{ fontWeight: 700 }} onClick={retryPrefsSync}>
              {t("prefsSync.retry")}
            </Button>
          }
          sx={{ alignItems: "center" }}
        >
          {t("prefsSync.failed")}
        </Alert>
      </Snackbar>
      <Snackbar
        open={bannerOpen}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: { xs: 4.5, sm: 5.5 } }}
      >
        <Alert
          severity="info"
          variant="filled"
          icon={<NotificationsActiveOutlinedIcon fontSize="inherit" />}
          onClose={dismissBanner}
          role="region"
          aria-label={t("banner.regionAriaLabel")}
          aria-live="polite"
          slotProps={{ closeButton: { "aria-label": t("banner.closeAriaLabel") } }}
          sx={(theme) => ({
            width: "100%",
            maxWidth: { xs: "min(calc(100vw - 24px), 100%)", sm: 560 },
            alignItems: "flex-start",
            py: 1.35,
            borderRadius: 2,
            ...(theme.palette.mode === "dark"
              ? {
                  bgcolor: theme.palette.info.dark,
                  color: theme.palette.info.contrastText,
                  boxShadow:
                    "0 4px 6px rgba(0,0,0,0.18), 0 14px 36px rgba(0,0,0,0.42), 0 24px 64px rgba(0,0,0,0.35)",
                }
              : {
                  bgcolor: "#d9e8f8",
                  color: "#0d2845",
                  border: "1px solid #b6d0ef",
                  boxShadow:
                    "0 2px 4px rgba(13, 40, 69, 0.06), 0 12px 28px rgba(13, 40, 69, 0.12), 0 20px 48px rgba(13, 40, 69, 0.14)",
                }),
            "& .MuiAlert-icon": {
              color: theme.palette.mode === "dark" ? "inherit" : theme.palette.primary.main,
              opacity: theme.palette.mode === "dark" ? 1 : 0.95,
            },
            "& .MuiAlert-action .MuiIconButton-root": {
              color: theme.palette.mode === "dark" ? "inherit" : "rgba(13, 40, 69, 0.55)",
            },
            "& .MuiAlert-message": {
              width: "100%",
              color: "inherit",
              pt: 0.2,
            },
          })}
        >
          <Typography
            variant="caption"
            sx={(theme) => ({
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              display: "block",
              color: theme.palette.mode === "dark" ? "inherit" : theme.palette.primary.main,
            })}
          >
            {t("banner.hintLabel")}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.65, lineHeight: 1.55, fontWeight: 500 }}>
            {t("banner.message")}
          </Typography>
          <Typography
            variant="caption"
            sx={(theme) => ({
              mt: 0.65,
              display: "block",
              lineHeight: 1.45,
              opacity: theme.palette.mode === "dark" ? 0.9 : 0.82,
            })}
          >
            {t("banner.optionalNote")}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ mt: 1.5 }}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="contained"
              size="small"
              color="inherit"
              onClick={async () => {
                await requestDesktopPermission();
                dismissBanner();
              }}
              sx={{
                fontWeight: 700,
                bgcolor: "common.white",
                color: "primary.dark",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              {t("banner.enable")}
            </Button>
            <Button
              variant="text"
              size="small"
              color="inherit"
              onClick={() => {
                navigate("/profile#profile-notifications");
                dismissBanner();
              }}
              sx={(theme) => ({
                fontWeight: 600,
                textDecoration: "underline",
                justifyContent: { xs: "center", sm: "flex-start" },
                color: theme.palette.mode === "dark" ? "inherit" : theme.palette.primary.dark,
              })}
            >
              {t("banner.settings")}
            </Button>
          </Stack>
        </Alert>
      </Snackbar>

      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: (theme) => theme.zIndex.snackbar + 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxWidth: 380,
          pointerEvents: "none",
          "& > *": { pointerEvents: "auto" },
        }}
      >
        {toasts.map((toast) => (
          <Snackbar
            key={toast.id}
            open={toast.open}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            sx={{ position: "relative", bottom: "auto", right: "auto" }}
          >
            <Alert
              severity={toast.severity || "info"}
              variant="filled"
              onClose={() =>
                setToasts((prev) => prev.filter((x) => x.id !== toast.id))
              }
              onClick={() => {
                if (toast.deepLink) {
                  if (toast.openLinkInNewTab) {
                    const url = absoluteUrlForWindowOpen(toast.deepLink);
                    if (url) window.open(url, "_blank", "noopener,noreferrer");
                  } else {
                    navigate(toast.deepLink);
                  }
                }
                setToasts((prev) => prev.filter((x) => x.id !== toast.id));
              }}
              sx={{ cursor: toast.deepLink ? "pointer" : "default", width: "100%" }}
            >
              <Box component="span" sx={{ fontWeight: 700, display: "block" }}>
                {toast.title}
              </Box>
              {toast.message ? (
                <Box component="span" sx={{ fontSize: "0.875rem", opacity: 0.95 }}>
                  {toast.message}
                </Box>
              ) : null}
            </Alert>
          </Snackbar>
        ))}
      </Box>
    </NotificationContext.Provider>
  );
}
